import React, { useEffect, useState, useRef } from "react";
import { styled, ThemeProvider, createTheme } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import VideoCall from "@mui/icons-material/VideoCall";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListItemButton from "@mui/material/ListItemButton";
import { Badge, Collapse, Box, Divider, Tooltip, Button } from "@mui/material";
import Loader from "react-loader-advanced";
import { removeAuthObjects } from "../../utils";
import appLogo from "../../assets/img/Fronic_Logo_white.png";
import sidenavBg from "../../assets/sidenav-bg.jpg";
import sidenavBg1 from "../../assets/sidenav-bg1.jpg";
import MusicLoader from "../../components/Common/MusicLoader";

//icons
import HomeIcon from "@mui/icons-material/Home";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import LibraryMusicIcon from "@mui/icons-material/LibraryMusic";
import AssessmentIcon from "@mui/icons-material/Assessment";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import LiveHelpIcon from "@mui/icons-material/LiveHelp";
import ConfirmModal from "../../components/Common/CommonModal";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { useHistory } from "react-router-dom";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import * as userActions from "../Profile/actions";
import * as commonActions from "../common/actions";
import BuildIcon from "@mui/icons-material/Build";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CampaignIcon from "@mui/icons-material/Campaign";
import Notification from "./Notification";
import GavelIcon from "@mui/icons-material/Gavel";
import VpnLockIcon from "@mui/icons-material/VpnLock";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import HearingIcon from "@mui/icons-material/Hearing";
import PlaylistPlayIcon from "@mui/icons-material/PlaylistPlay";
import LyricsIcon from "@mui/icons-material/Lyrics";
import HandshakeIcon from "@mui/icons-material/Handshake";
import EqualizerIcon from "@mui/icons-material/Equalizer";
import useOutsideAlerter from "../../hooks/useOutsideAlerter";
import { fronicMedia_FAQ, webSeriesDestribution, USER_TRACKING_CONFIG } from "../../constants";

const drawerWidth = 280;

// Randomly select background image on load
const randomBg = Math.random() < 0.5 ? sidenavBg : sidenavBg1;

// Modern color palette
const colors = {
  primary: '#1a73e8',  // Modern blue
  primaryDark: '#1557b0',
  secondary: '#00bfa5',  // Teal accent
  background: {
    main: '#1e1e2e',  // Dark navy
    drawer: '#262637',  // Slightly lighter
    hover: 'rgba(255, 255, 255, 0.08)',
  },
  text: {
    primary: '#ffffff',
    secondary: 'rgba(255, 255, 255, 0.7)',
  },
  border: 'rgba(255, 255, 255, 0.12)',
};

// Create MUI theme
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: colors.primary,
    },
    secondary: {
      main: colors.secondary,
    },
  },
  typography: {
    fontFamily: "'DM Sans', sans-serif",
  },
  components: {
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: 'transparent !important',
            background: 'transparent !important',
          },
          '&.Mui-selected:hover': {
            backgroundColor: 'transparent !important',
            background: 'transparent !important',
          },
        },
      },
    },
  },
});

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  background: colors.background.drawer,
  backgroundImage: `linear-gradient(rgba(38, 38, 55, 0.75), rgba(38, 38, 55, 0.75)), url(${randomBg})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  borderRight: `1px solid ${colors.border}`,
  display: 'flex',
  flexDirection: 'column',
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  background: colors.background.drawer,
  backgroundImage: `linear-gradient(rgba(38, 38, 55, 0.75), rgba(38, 38, 55, 0.75)), url(${randomBg})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  borderRight: `1px solid ${colors.border}`,
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
  display: 'flex',
  flexDirection: 'column',
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(0, 1),
  backgroundColor: 'rgba(38, 38, 55, 0.15)',
  backdropFilter: 'blur(2px)',
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  background: colors.background.main,
  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }),
);

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
  minHeight: 48,
  borderRadius: '8px',
  margin: '4px 8px',
  color: colors.text.secondary,
  transition: 'all 0.2s ease-in-out',
  backgroundColor: 'rgba(38, 38, 55, 0.15) !important',
  backdropFilter: 'blur(2px)',
  '&:hover': {
    backgroundColor: `${colors.background.hover} !important`,
    color: colors.text.primary,
    transform: 'translateX(4px)',
  },
  ...(active && {
    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%) !important`,
    backgroundColor: `${colors.primary} !important`,
    color: `${colors.text.primary} !important`,
    boxShadow: '0 4px 12px rgba(26, 115, 232, 0.3)',
    '&:hover': {
      background: `linear-gradient(135deg, ${colors.primaryDark} 0%, ${colors.primary} 100%) !important`,
      backgroundColor: `${colors.primaryDark} !important`,
      transform: 'translateX(0)',
    },
  }),
  '&.Mui-selected, &.Mui-selected:hover, &.Mui-focusVisible': {
    backgroundColor: 'rgba(38, 38, 55, 0.15) !important',
    background: 'rgba(38, 38, 55, 0.15) !important',
  },
}));

const StyledListItemIcon = styled(ListItemIcon)(({ active }) => ({
  minWidth: 0,
  marginRight: 12,
  justifyContent: 'center',
  color: active ? colors.text.primary : colors.text.secondary,
}));

const NestedListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
  minHeight: 40,
  paddingLeft: theme.spacing(4),
  borderRadius: '8px',
  margin: '2px 8px',
  color: colors.text.secondary,
  transition: 'all 0.2s ease-in-out',
  backgroundColor: 'rgba(38, 38, 55, 0.15) !important',
  backdropFilter: 'blur(2px)',
  '&:hover': {
    backgroundColor: `${colors.background.hover} !important`,
    color: colors.text.primary,
    paddingLeft: theme.spacing(4.5),
  },
  ...(active && {
    background: `linear-gradient(135deg, ${colors.secondary} 0%, ${colors.primary} 100%) !important`,
    backgroundColor: `${colors.secondary} !important`,
    color: `${colors.text.primary} !important`,
    boxShadow: '0 2px 8px rgba(0, 191, 165, 0.3)',
    '&:hover': {
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%) !important`,
      backgroundColor: `${colors.primary} !important`,
    },
  }),
  '&.Mui-selected, &.Mui-selected:hover, &.Mui-focusVisible': {
    backgroundColor: 'rgba(38, 38, 55, 0.15) !important',
    background: 'rgba(38, 38, 55, 0.15) !important',
  },
}));

export default function MainContainer(props) {
  const { path } = props.match;
  const currentPath = props.location.pathname;
  const history = useHistory();

  // Initialize drawer state from localStorage or default based on screen size
  const getInitialDrawerState = () => {
    // Force drawer open for create release routes
    const currentPath = window.location.pathname;
    const isCreateReleasePath = currentPath.startsWith('/create_releases') ||
                                 currentPath.startsWith('/create_video_releases') ||
                                 currentPath === '/select_release';

    if (isCreateReleasePath && window.innerWidth >= 700) {
      return true;
    }

    const savedState = localStorage.getItem('drawerOpen');
    if (savedState !== null) {
      return savedState === 'true';
    }
    return window.innerWidth >= 700;
  };

  const [open, setOpen] = useState(getInitialDrawerState);
  const [expendTools, setExpendTools] = useState(false);
  const [expendRights, setExpendRights] = useState(false);
  const [expendMarketing, setExpandMarketing] = useState(false);

  const [loader, setLoader] = useState(false);
  const [confirmOpen, setConfimOpen] = useState(false);
  const [userRevenue, setUserRevenue] = useState(0.0);
  const [conversionRate, setConversionRate] = useState();
  const [openNotification, setOpenNotification] = useState(false);
  const [notificationLoader, setNotificationLoader] = useState(false);
  const [notification, setNotification] = useState([]);
  const [offset, setOffset] = useState(0);
  const [runPagination, setRunPagination] = useState(false);
  const [showBadge, setShowBadge] = useState(false);
  const [analyticsAccess, setAnalyticsAccess] = useState(false);
  const dropdownRef = useRef(null);
  const clickedOutside = useOutsideAlerter(dropdownRef);
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (clickedOutside) {
      if (openNotification) {
        setOpenNotification(false);
      }
    }
  }, [clickedOutside]);

  useEffect(() => {
    // Fetch user data
    userActions.getUser().then((res) => {
      setUserRevenue((res.body && res.body.revenue && res.body.revenue) || 0);
      setConversionRate((res.body && res.body.conversionRate && res.body.conversionRate) || 0);
      setAnalyticsAccess(res?.body?.analytics === 'true');
    });

    // Check notification bell
    commonActions.checkNotificationBell().then((res) => {
      setShowBadge(
        res &&
          res.body &&
          res.body.notification_icon &&
          res.body.notification_icon === "seen"
          ? false
          : true
      );
    });

    // Set up notification polling
    const notificationInterval = setInterval(() => {
      commonActions.checkNotificationBell().then((res) => {
        setShowBadge(
          res &&
            res.body &&
            res.body.notification_icon &&
            res.body.notification_icon === "seen"
            ? false
            : true
        );
      });
    }, 420000);

    // Set up user activity tracking (if enabled)
    let trackingInterval = null;
    let initialTrackingTimeout = null;

    if (USER_TRACKING_CONFIG.ENABLED) {
      // Delay initial tracking by 10 seconds to reduce page load
      initialTrackingTimeout = setTimeout(() => {
        commonActions.trackUserActivity({
          page: props.location.pathname,
          timestamp: new Date().toISOString()
        }).catch((err) => {
          console.error('Error tracking activity:', err);
        });

        // Set up periodic tracking after initial call
        trackingInterval = setInterval(() => {
          commonActions.trackUserActivity({
            page: props.location.pathname,
            timestamp: new Date().toISOString()
          }).catch((err) => {
            console.error('Error tracking activity:', err);
          });
        }, USER_TRACKING_CONFIG.HEARTBEAT_INTERVAL);
      }, props.location.pathname === '/' ? 20000 : 10000); // 20s for dashboard, 10s for others
    }

    return () => {
      clearInterval(notificationInterval);
      if (initialTrackingTimeout) clearTimeout(initialTrackingTimeout);
      if (trackingInterval) clearInterval(trackingInterval);
    };
  }, []);

  // Track page changes when user navigates (skip initial mount)
  useEffect(() => {
    // Skip API call on initial mount (10s delay handles that)
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (USER_TRACKING_CONFIG.ENABLED) {
      commonActions.trackUserActivity({
        page: props.location.pathname,
        timestamp: new Date().toISOString()
      }).catch((err) => {
        console.error('Error tracking page change:', err);
      });
    }
  }, [props.location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      const isSmallScreen = window.innerWidth < 700;
      if (isSmallScreen && open) {
        setOpen(false);
      } else if (!isSmallScreen && !open) {
        // Only auto-open on large screens if no saved preference
        const savedState = localStorage.getItem('drawerOpen');
        if (savedState === null) {
          setOpen(true);
        }
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [open]);

  // Ensure drawer is open on create release routes
  useEffect(() => {
    const isCreateReleasePath = currentPath.startsWith('/create_releases') ||
                                 currentPath.startsWith('/create_video_releases') ||
                                 currentPath === '/select_release';

    if (isCreateReleasePath) {
      if (window.innerWidth >= 700) {
        // Force drawer open on these routes
        setTimeout(() => {
          setOpen(true);
          localStorage.setItem('drawerOpen', 'true');
        }, 100);
      }
    }
  }, [currentPath]);

  const handleDrawerOpen = () => {
    setOpen(true);
    localStorage.setItem('drawerOpen', 'true');
  };

  const handleDrawerClose = () => {
    setOpen(false);
    localStorage.setItem('drawerOpen', 'false');
  };

  const listItems = [
    {
      label: "Dashboard",
      icon: <HomeIcon />,
      selected: currentPath === "/",
      path: "/",
    },
    {
      label: "Create Release",
      icon: <NewReleasesIcon />,
      selected:
        currentPath.startsWith("/create_releases") ||
        currentPath === "/select_release" ||
        currentPath === "/create_video_releases",
      path: "/select_release",
    },
    {
      label: "Catalog",
      icon: <LibraryMusicIcon />,
      selected:
        currentPath.startsWith("/catalog") ||
        currentPath === "/video-catalog",
      path: "/catalog",
    },
    {
      label: "Reports",
      icon: <AssessmentIcon />,
      selected: currentPath === "/reports",
      path: "/reports",
    },
    {
      label: "Tickets",
      icon: <SupportAgentIcon />,
      selected: currentPath.startsWith("/tickets"),
      path: "/tickets",
    },
    {
      label: "Withdraw",
      icon: <AccountBalanceWalletIcon />,
      selected: currentPath === "/withdrawals",
      path: "/withdrawals",
    },
    {
      label: "Rights Manager",
      icon: <VpnLockIcon />,
      selected: currentPath.startsWith("/rights"),
      path: "/rights",
    },
    {
      label: "Tools",
      icon: <BuildIcon />,
      selected: currentPath.startsWith("/tools") || currentPath === "/analytics",
      path: "/tools",
    },
    {
      label: "Marketing",
      icon: <CampaignIcon />,
      selected: currentPath.startsWith("/marketing"),
      path: "/marketing",
    },
    {
      label: "Invite",
      icon: <PersonAddIcon />,
      selected: currentPath === "/invite",
      path: "/invite",
    },
    {
      label: "Distribute Web Series",
      icon: <VideoCall />,
      selected: false,
      path: webSeriesDestribution,
    },
    {
      label: "FAQ",
      icon: <LiveHelpIcon />,
      selected: false,
      path: fronicMedia_FAQ,
    },
    {
      label: "Logout",
      icon: <ExitToAppIcon />,
      path: "/login",
    },
  ];

  const header = () => {
    // const { path } = props.match
    switch (path) {
      case "/":
        return "Dashboard";
      case "/create_releases/:id":
      case "/create_releases":
        return "Create Release";
      case "/create_video_releases":
        return "Create Video Release";
      case "/catalog/:id":
      case "/catalog":
        return "Audio Catalog";
      case "/video-catalog":
        return "Video Catalog";
      // case '/Social Media':
      //     return 'social_media'
      case "/tickets/:id":
      case "/tickets":
        return "Tickets";
      case "/reports":
        return "Reports";
      case "/analytics":
        return "Analytics";
      case "/invite":
        return "Invite";
      case "/profile":
        return "My Profile";
      case "/withdrawals":
        return "Withdraw Funds";
      case "/rights/ugc-claims":
        return "UGC Claims";
      case "/rights/whitelist":
        return "Whitelist";
      case "/tools/profile-linking":
        return "Profile Linking";

      case "/maketing/playlist-pitchment":
        return "Playlist Pitchment";
      case "/maketing/promotions":
        return "Promotions";

      case "/tools/distribute-lyrics":
        return "Distribute Lyrics";
      default: {
        return "";
      }
    }
  };

  const handleConfirmClickOpen = () => {
    setConfimOpen(true);
  };

  const handleConfirmClose = () => {
    setConfimOpen(false);
  };

  const onConfirmLogout = () => {
    onSetLoader(true);
    removeAuthObjects();
    // actions.clearStore()
    setTimeout(() => {
      onSetLoader(false);
      // return props.history.push('/login')
      window.open("https://fronicmedia.com", "_self");
    }, 1000);
  };

  const onSetLoader = (action) => {
    setLoader(action);
  };

  const onOpenNotification = () => {
    if (openNotification || clickedOutside) return;
    setOpenNotification(true);
    if (!notification.length || showBadge) {
      setNotification([]);
      getAllNotification(0);
    }
  };

  const getAllNotification = async (offset = 0) => {
    setNotificationLoader(true);
    await commonActions.getNotificationsForUser(offset).then((res) => {
      setShowBadge(false);
      setRunPagination(
        Array.isArray(res.body) && res.body.length === 0 ? false : true || true
      );
      setNotification((prev) =>
        Array.isArray(res.body) ? [...prev, ...res.body] : prev
      );
    });
    setNotificationLoader(false);
  };

  const scrollingNotification = (e) => {
    const { scrollHeight = 0, scrollTop = 0, scrollWidth = 0 } = e.target || {};

    if (
      scrollHeight - (scrollTop + scrollWidth) < 70 &&
      !notificationLoader &&
      runPagination
    ) {
      setOffset((prev) => prev + 20);
      getAllNotification(offset + 20);
    }
  };

  const LoaderStyle = {
    height: "100%",
    overflow: "hidden",
  };

  return (
    <ThemeProvider theme={theme}>
      <Loader
        className="main-loader"
        style={loader ? LoaderStyle : null}
        message={<MusicLoader />}
        show={loader}
      >
        <Box sx={{ display: 'flex' }}>
          <CssBaseline />
          <AppBar position="fixed" open={open}>
            <Toolbar>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                onClick={handleDrawerOpen}
                edge="start"
                sx={{
                  marginRight: 5,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(26, 115, 232, 0.15)',
                    transform: 'rotate(90deg)',
                  },
                  ...(open && { display: 'none' }),
                }}
              >
                <MenuIcon />
              </IconButton>
              <div
                style={{
                  width: "100%",
                  justifyContent: "space-between",
                  display: "flex",
                }}
              >
                <Typography
                  variant="h6"
                  noWrap
                  sx={{
                    alignSelf: "center",
                    fontFamily: "'DM Sans', sans-serif",
                    fontWeight: 700,
                    fontSize: '1.25rem',
                    color: colors.text.primary,
                    letterSpacing: '-0.5px',
                  }}
                >
                  {header()}
                </Typography>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    position: "relative",
                  }}
                >
                  {/* Profile Button */}
                  <Button
                    onClick={() => history.push("/profile")}
                    startIcon={<AccountCircleIcon sx={{ fontSize: '1.5rem' }} />}
                    sx={{
                      ml: 1,
                      color: colors.text.primary,
                      textTransform: 'none',
                      fontFamily: "'DM Sans', sans-serif",
                      fontWeight: 600,
                      fontSize: '1rem',
                      borderRadius: '8px',
                      padding: '8px 18px',
                      display: 'flex',
                      alignItems: 'center',
                      transition: 'all 0.3s ease',
                      '& .MuiButton-startIcon': {
                        marginRight: '8px',
                        display: 'flex',
                        alignItems: 'center',
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(26, 115, 232, 0.15)',
                        '& svg, & span': {
                          color: colors.primary,
                        },
                      },
                    }}
                  >
                    My Profile
                  </Button>

                  {/* Notification Icon */}
                  <Tooltip title="Notifications" placement="bottom">
                    <IconButton
                      onClick={onOpenNotification}
                      sx={{
                        ml: 0.5,
                        color: colors.text.primary,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          backgroundColor: 'rgba(26, 115, 232, 0.15)',
                          transform: 'scale(1.1)',
                          '& svg': {
                            color: colors.primary,
                          },
                        },
                      }}
                    >
                      <Badge
                        badgeContent={showBadge ? "new" : 0}
                        sx={{
                          '& .MuiBadge-badge': {
                            background: 'linear-gradient(135deg, #00bfa5 0%, #1a73e8 100%)',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.65rem',
                            fontFamily: "'DM Sans', sans-serif",
                            boxShadow: '0 2px 6px rgba(0, 191, 165, 0.4)',
                          },
                        }}
                      >
                        <NotificationsIcon sx={{ fontSize: 26 }} />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                  {openNotification ? (
                    <div
                      className="notification-container"
                      ref={dropdownRef}
                      onScroll={scrollingNotification}
                    >
                      <Notification
                        notification={notification}
                        notificationLoader={notificationLoader}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            </Toolbar>
          </AppBar>
          <Drawer variant="permanent" open={open}>
            <DrawerHeader>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  justifyContent: 'space-between',
                  py: 1,
                }}
              >
                <Box
                  component="img"
                  src={appLogo}
                  alt="Fronic Media"
                  onClick={() => props.history.push("/")}
                  sx={{
                    height: open ? 40 : 28,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    filter: 'brightness(1.1)',
                    '&:hover': {
                      transform: 'scale(1.05)',
                    },
                  }}
                />
                <IconButton
                  onClick={handleDrawerClose}
                  sx={{
                    color: colors.text.primary,
                    '&:hover': {
                      backgroundColor: colors.background.hover,
                    },
                  }}
                >
                  <ChevronLeftIcon />
                </IconButton>
              </Box>
            </DrawerHeader>
            <Divider sx={{ borderColor: colors.border, mb: 1 }} />
            <List style={{ padding: 0, flex: 1 }}>
              {listItems.map((text, index) => {
                const isActive = text.selected;
                if (text.label === "Logout") {
                  return (
                    <Tooltip title={!open ? text.label : ""} placement="right" key={text.label}>
                      <StyledListItemButton
                        active={isActive ? 1 : 0}
                        onClick={handleConfirmClickOpen}
                        sx={{ justifyContent: open ? 'initial' : 'center' }}
                      >
                        <StyledListItemIcon active={isActive ? 1 : 0} sx={{ mr: open ? 1.5 : 'auto' }}>
                          {text.icon}
                        </StyledListItemIcon>
                        <ListItemText
                          primary={text.label}
                          sx={{
                            opacity: open ? 1 : 0,
                            color: 'inherit',
                            '& .MuiTypography-root': {
                              fontFamily: "'DM Sans', sans-serif",
                              fontWeight: isActive ? 600 : 400,
                            }
                          }}
                        />
                      </StyledListItemButton>
                    </Tooltip>
                  );
                } else if (text.label === "FAQ") {
                  return (
                    <Tooltip title={!open ? text.label : ""} placement="right" key={text.label}>
                      <Box
                        component="a"
                        href={text.path}
                        target="_blank"
                        rel="noreferrer"
                        sx={{ textDecoration: 'none' }}
                      >
                        <StyledListItemButton
                          active={isActive ? 1 : 0}
                          sx={{ justifyContent: open ? 'initial' : 'center' }}
                        >
                          <StyledListItemIcon active={isActive ? 1 : 0} sx={{ mr: open ? 1.5 : 'auto' }}>
                            {text.icon}
                          </StyledListItemIcon>
                          <ListItemText
                            primary={text.label}
                            sx={{
                              opacity: open ? 1 : 0,
                              color: 'inherit',
                              '& .MuiTypography-root': {
                                fontFamily: "'DM Sans', sans-serif",
                                fontWeight: isActive ? 600 : 400,
                              }
                            }}
                          />
                        </StyledListItemButton>
                      </Box>
                    </Tooltip>
                  );
                } else if (text.label === "Distribute Web Series") {
                  return (
                    <Tooltip title={!open ? text.label : ""} placement="right" key={text.label}>
                      <Box
                        component="a"
                        href={text.path}
                        target="_blank"
                        rel="noreferrer"
                        sx={{ textDecoration: 'none' }}
                      >
                        <StyledListItemButton
                          active={isActive ? 1 : 0}
                          sx={{ justifyContent: open ? 'initial' : 'center' }}
                        >
                          <StyledListItemIcon active={isActive ? 1 : 0} sx={{ mr: open ? 1.5 : 'auto' }}>
                            {text.icon}
                          </StyledListItemIcon>
                          <ListItemText
                            primary={text.label}
                            sx={{
                              opacity: open ? 1 : 0,
                              color: 'inherit',
                              '& .MuiTypography-root': {
                                fontFamily: "'DM Sans', sans-serif",
                                fontWeight: isActive ? 600 : 400,
                              }
                            }}
                          />
                        </StyledListItemButton>
                      </Box>
                    </Tooltip>
                  );
                } else if (text.label === "Tools") {
                  return (
                    <Box key={text.label}>
                      <Tooltip title={!open ? text.label : ""} placement="right">
                        <StyledListItemButton
                          onClick={() => setExpendTools((prev) => !prev)}
                          sx={{ justifyContent: open ? 'initial' : 'center' }}
                        >
                          <StyledListItemIcon sx={{ mr: open ? 1.5 : 'auto' }}>
                            {text.icon}
                          </StyledListItemIcon>
                          <ListItemText
                            primary={text.label}
                            sx={{
                              opacity: open ? 1 : 0,
                              color: 'inherit',
                              '& .MuiTypography-root': {
                                fontFamily: "'DM Sans', sans-serif",
                              }
                            }}
                          />
                          {open && (expendTools ? <ExpandLess sx={{ color: colors.text.secondary }} /> : <ExpandMore sx={{ color: colors.text.secondary }} />)}
                        </StyledListItemButton>
                      </Tooltip>
                      <Collapse in={expendTools && open} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                          <Tooltip title={!open ? "Profile Linking" : ""} placement="right">
                            <NestedListItemButton
                              active={currentPath === "/tools/profile-linking" ? 1 : 0}
                              onClick={() => props.history.push("/tools/profile-linking")}
                              sx={{ justifyContent: open ? 'initial' : 'center' }}
                            >
                              <StyledListItemIcon active={currentPath === "/tools/profile-linking" ? 1 : 0} sx={{ mr: open ? 1.5 : 'auto' }}>
                                <SupervisorAccountIcon />
                              </StyledListItemIcon>
                              <ListItemText
                                primary="Profile Linking"
                                sx={{
                                  opacity: open ? 1 : 0,
                                  color: 'inherit',
                                  '& .MuiTypography-root': {
                                    fontFamily: "'DM Sans', sans-serif",
                                    fontSize: '0.875rem',
                                  }
                                }}
                              />
                            </NestedListItemButton>
                          </Tooltip>
                          <Tooltip title={!open ? "Distribute Lyrics" : ""} placement="right">
                            <NestedListItemButton
                              active={currentPath === "/tools/distribute-lyrics" ? 1 : 0}
                              onClick={() => props.history.push("/tools/distribute-lyrics")}
                              sx={{ justifyContent: open ? 'initial' : 'center' }}
                            >
                              <StyledListItemIcon active={currentPath === "/tools/distribute-lyrics" ? 1 : 0} sx={{ mr: open ? 1.5 : 'auto' }}>
                                <LyricsIcon />
                              </StyledListItemIcon>
                              <ListItemText
                                primary="Distribute Lyrics"
                                sx={{
                                  opacity: open ? 1 : 0,
                                  color: 'inherit',
                                  '& .MuiTypography-root': {
                                    fontFamily: "'DM Sans', sans-serif",
                                    fontSize: '0.875rem',
                                  }
                                }}
                              />
                            </NestedListItemButton>
                          </Tooltip>
                          {analyticsAccess && (
                            <Tooltip title={!open ? "Analytics" : ""} placement="right">
                              <NestedListItemButton
                                active={currentPath === "/analytics" ? 1 : 0}
                                onClick={() => props.history.push("/analytics")}
                                sx={{ justifyContent: open ? 'initial' : 'center' }}
                              >
                                <StyledListItemIcon active={currentPath === "/analytics" ? 1 : 0} sx={{ mr: open ? 1.5 : 'auto' }}>
                                  <EqualizerIcon />
                                </StyledListItemIcon>
                                <ListItemText
                                  primary="Analytics"
                                  sx={{
                                    opacity: open ? 1 : 0,
                                    color: 'inherit',
                                    '& .MuiTypography-root': {
                                      fontFamily: "'DM Sans', sans-serif",
                                      fontSize: '0.875rem',
                                    }
                                  }}
                                />
                              </NestedListItemButton>
                            </Tooltip>
                          )}
                        </List>
                      </Collapse>
                    </Box>
                  );
                } else if (text.label === "Rights Manager") {
                  return (
                    <Box key={text.label}>
                      <Tooltip title={!open ? text.label : ""} placement="right">
                        <StyledListItemButton
                          onClick={() => setExpendRights((prev) => !prev)}
                          sx={{ justifyContent: open ? 'initial' : 'center' }}
                        >
                          <StyledListItemIcon sx={{ mr: open ? 1.5 : 'auto' }}>
                            {text.icon}
                          </StyledListItemIcon>
                          <ListItemText
                            primary={text.label}
                            sx={{
                              opacity: open ? 1 : 0,
                              color: 'inherit',
                              '& .MuiTypography-root': {
                                fontFamily: "'DM Sans', sans-serif",
                              }
                            }}
                          />
                          {open && (expendRights ? <ExpandLess sx={{ color: colors.text.secondary }} /> : <ExpandMore sx={{ color: colors.text.secondary }} />)}
                        </StyledListItemButton>
                      </Tooltip>
                      <Collapse in={expendRights && open} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                          <Tooltip title={!open ? "UGC Claims" : ""} placement="right">
                            <NestedListItemButton
                              active={currentPath === "/rights/ugc-claims" ? 1 : 0}
                              onClick={() => props.history.push("/rights/ugc-claims")}
                              sx={{ justifyContent: open ? 'initial' : 'center' }}
                            >
                              <StyledListItemIcon active={currentPath === "/rights/ugc-claims" ? 1 : 0} sx={{ mr: open ? 1.5 : 'auto' }}>
                                <GavelIcon />
                              </StyledListItemIcon>
                              <ListItemText
                                primary="UGC Claims"
                                sx={{
                                  opacity: open ? 1 : 0,
                                  color: 'inherit',
                                  '& .MuiTypography-root': {
                                    fontFamily: "'DM Sans', sans-serif",
                                    fontSize: '0.875rem',
                                  }
                                }}
                              />
                            </NestedListItemButton>
                          </Tooltip>
                          <Tooltip title={!open ? "Whitelist" : ""} placement="right">
                            <NestedListItemButton
                              active={currentPath === "/rights/whitelist" ? 1 : 0}
                              onClick={() => props.history.push("/rights/whitelist")}
                              sx={{ justifyContent: open ? 'initial' : 'center' }}
                            >
                              <StyledListItemIcon active={currentPath === "/rights/whitelist" ? 1 : 0} sx={{ mr: open ? 1.5 : 'auto' }}>
                                <PlaylistPlayIcon />
                              </StyledListItemIcon>
                              <ListItemText
                                primary="Whitelist"
                                sx={{
                                  opacity: open ? 1 : 0,
                                  color: 'inherit',
                                  '& .MuiTypography-root': {
                                    fontFamily: "'DM Sans', sans-serif",
                                    fontSize: '0.875rem',
                                  }
                                }}
                              />
                            </NestedListItemButton>
                          </Tooltip>
                        </List>
                      </Collapse>
                    </Box>
                  );
                } else if (text.label === "Marketing") {
                  return (
                    <Box key={text.label}>
                      <Tooltip title={!open ? text.label : ""} placement="right">
                        <StyledListItemButton
                          onClick={() => setExpandMarketing((prev) => !prev)}
                          sx={{ justifyContent: open ? 'initial' : 'center' }}
                        >
                          <StyledListItemIcon sx={{ mr: open ? 1.5 : 'auto' }}>
                            {text.icon}
                          </StyledListItemIcon>
                          <ListItemText
                            primary={text.label}
                            sx={{
                              opacity: open ? 1 : 0,
                              color: 'inherit',
                              '& .MuiTypography-root': {
                                fontFamily: "'DM Sans', sans-serif",
                              }
                            }}
                          />
                          {open && (expendMarketing ? <ExpandLess sx={{ color: colors.text.secondary }} /> : <ExpandMore sx={{ color: colors.text.secondary }} />)}
                        </StyledListItemButton>
                      </Tooltip>
                      <Collapse in={expendMarketing && open} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                          <Tooltip title={!open ? "Playlist Pitchment" : ""} placement="right">
                            <NestedListItemButton
                              active={currentPath === "/marketing/playlist-pitchment" ? 1 : 0}
                              onClick={() => props.history.push("/marketing/playlist-pitchment")}
                              sx={{ justifyContent: open ? 'initial' : 'center' }}
                            >
                              <StyledListItemIcon active={currentPath === "/marketing/playlist-pitchment" ? 1 : 0} sx={{ mr: open ? 1.5 : 'auto' }}>
                                <HearingIcon />
                              </StyledListItemIcon>
                              <ListItemText
                                primary="Playlist Pitchment"
                                sx={{
                                  opacity: open ? 1 : 0,
                                  color: 'inherit',
                                  '& .MuiTypography-root': {
                                    fontFamily: "'DM Sans', sans-serif",
                                    fontSize: '0.875rem',
                                  }
                                }}
                              />
                            </NestedListItemButton>
                          </Tooltip>
                          <Tooltip title={!open ? "Promotions" : ""} placement="right">
                            <NestedListItemButton
                              active={currentPath === "/marketing/promotions" ? 1 : 0}
                              onClick={() => props.history.push("/marketing/promotions")}
                              sx={{ justifyContent: open ? 'initial' : 'center' }}
                            >
                              <StyledListItemIcon active={currentPath === "/marketing/promotions" ? 1 : 0} sx={{ mr: open ? 1.5 : 'auto' }}>
                                <HandshakeIcon />
                              </StyledListItemIcon>
                              <ListItemText
                                primary="Promotions"
                                sx={{
                                  opacity: open ? 1 : 0,
                                  color: 'inherit',
                                  '& .MuiTypography-root': {
                                    fontFamily: "'DM Sans', sans-serif",
                                    fontSize: '0.875rem',
                                  }
                                }}
                              />
                            </NestedListItemButton>
                          </Tooltip>
                        </List>
                      </Collapse>
                    </Box>
                  );
                } else {
                  return (
                    <Tooltip title={!open ? text.label : ""} placement="right" key={text.label}>
                      <StyledListItemButton
                        active={isActive ? 1 : 0}
                        selected={false}
                        disableRipple={false}
                        onClick={() => props.history.push(text.path)}
                        sx={{
                          justifyContent: open ? 'initial' : 'center',
                          ...(isActive && {
                            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%) !important`,
                            backgroundColor: `${colors.primary} !important`,
                          }),
                        }}
                      >
                        <StyledListItemIcon active={isActive ? 1 : 0} sx={{ mr: open ? 1.5 : 'auto' }}>
                          {text.icon}
                        </StyledListItemIcon>
                        <ListItemText
                          primary={text.label}
                          sx={{
                            opacity: open ? 1 : 0,
                            color: 'inherit',
                            '& .MuiTypography-root': {
                              fontFamily: "'DM Sans', sans-serif",
                              fontWeight: isActive ? 600 : 400,
                            }
                          }}
                        />
                      </StyledListItemButton>
                    </Tooltip>
                  );
                }
              })}
            </List>

            {/* Ambala Production Footer */}
            <Box
              sx={{
                px: 2,
                py: 2,
                mt: 'auto',
                borderTop: `1px solid ${colors.border}`,
                opacity: open ? 1 : 0,
                transition: 'opacity 0.3s',
                backgroundColor: 'rgba(38, 38, 55, 0.15)',
                backdropFilter: 'blur(2px)',
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.65rem',
                  color: colors.text.secondary,
                  textAlign: 'center',
                  lineHeight: 1.4,
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                An{' '}
                <Box
                  component="span"
                  onClick={() =>
                    window.open("https://ambalaproductions.in/", "_blank")
                  }
                  sx={{
                    color: colors.secondary,
                    cursor: 'pointer',
                    fontWeight: 600,
                    transition: 'all 0.2s',
                    '&:hover': {
                      color: colors.primary,
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Ambala Production's
                </Box>{' '}
                Product
              </Typography>
            </Box>
          </Drawer>
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              p: 3,
              pt: '88px',
              backgroundColor: '#f5f5f7',
              minHeight: '100vh',
            }}
          >
            <DrawerHeader sx={{ display: 'none' }} />
            {React.cloneElement(props.children, { setLoader: onSetLoader })}
          </Box>
          <ConfirmModal
            message={`Are you sure you want to Logout?`}
            open={confirmOpen}
            title={`Confirm Logout`}
            onSubmitConfirm={onConfirmLogout}
            handleClose={handleConfirmClose}
          ></ConfirmModal>
        </Box>
      </Loader>
    </ThemeProvider>
  );
}
